import { AuthApi } from '@api';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Typography,
} from '@mui/material';
import { createElement, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ExpandMore from '@mui/icons-material/ExpandMore';
import BasicDialog, { BasicDialogContent } from '../utils/dialogs/BasicDialog';
import { useNavigate } from 'react-router-dom';
import { SettingsConfig, settingsConfig } from './SettingsAccordionConfig';
import { SupervisorContext } from 'src/contexts/SupervisorContext';
import { useThemePreference } from '@/contexts/ThemePreferenceContext';
import { getThemeTokens } from '@/utils/appThemes';
import { useAiAvailable } from '@/utils/ai';

const SettingsAccordion = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isSupervising } = useContext(SupervisorContext);
  const aiAvailable = useAiAvailable();
  const { selectedTheme, isDarkMode } = useThemePreference();
  const tk = getThemeTokens(selectedTheme, isDarkMode);

  const [dialog, setDialog] = useState<BasicDialogContent | false>(false);
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChange =
    (setting: SettingsConfig) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      if (setting.dialog) setDialog(setting.dialog);
      setExpanded(isExpanded ? setting.id : false);
    };

  const showSetting = (setting: SettingsConfig) => {
    if (setting.disabled) return false;
    if (setting.requiresAi && !aiAvailable) return false;
    if (setting.onlyAsSupervisor && !isSupervising) return false;
    if (setting.hideAsSupervisor && isSupervising) return false;
    return true;
  };

  const reloadApp = () => {
    navigate('/login');
    window.location.reload();
  };

  const handleLogout = async () => {
    await new AuthApi().logout();
    reloadApp();
  };

  const onDialogConfirm = () => {
    if (dialog && dialog.title === 'Logout') handleLogout();
  };

  return (
    <div>
      {settingsConfig.map((setting, index) => {
        if (!showSetting(setting)) return null;

        if (setting.toggleComponent) {
          return (
            <Box key={`settingsToggle-${setting.id}`}>
              {createElement(setting.toggleComponent, setting)}
            </Box>
          );
        }

        return (
          <Accordion
            expanded={expanded === setting.id}
            onChange={handleChange(setting)}
            key={`settingsAccordion-${setting.id}`}
            sx={{
              bgcolor: tk.card,
              color: tk.text,
              border: `1px solid ${tk.border}`,
              borderRadius: '12px !important',
              mb: 1,
              '&:before': { display: 'none' },
              boxShadow: 'none',
            }}
          >
            {setting.accordionHeader ? (
              <Box>{createElement(setting.accordionHeader, setting)}</Box>
            ) : (
              <AccordionSummary
                expandIcon={
                  setting.accordionContent ? <ExpandMore sx={{ color: tk.textSoft }} /> : null
                }
              >
                <Stack
                  direction="row"
                  width="100%"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="h6" sx={{ color: setting.titleColor ?? tk.primary }}>
                    {t(setting.title)}
                  </Typography>
                  {setting.icon ? createElement(setting.icon) : null}
                </Stack>
              </AccordionSummary>
            )}
            {setting.accordionContent ? (
              <AccordionDetails>{createElement(setting.accordionContent)}</AccordionDetails>
            ) : null}
          </Accordion>
        );
      })}
      <BasicDialog
        open={!!dialog}
        onClose={() => setDialog(false)}
        onConfirm={onDialogConfirm}
        content={dialog ? dialog : ({} as BasicDialogContent)}
      />
    </div>
  );
};

export default SettingsAccordion;
